import React from 'react';
import { Link } from 'react-router-dom';

import LegalDocumentPage, { Section, SectionTitle } from '../components/LegalDocumentPage';
import { LEGAL, LEGAL_ROUTES } from '../data/legalContent';

const TermsOfService = () => (
  <LegalDocumentPage
    title="Terms of Service"
    description="Terms for using the FounderDNA founder assessment."
    path={LEGAL_ROUTES.terms}
    lastUpdated={LEGAL.lastUpdated}
  >
    <Section>
      <p>
        These Terms of Service (&quot;Terms&quot;) govern your use of {LEGAL.siteName} at {LEGAL.siteUrl}. By using the
        site, you agree to these Terms. If you do not agree, do not use the service.
      </p>
    </Section>

    <Section>
      <SectionTitle>What FounderDNA provides</SectionTitle>
      <p>
        {LEGAL.siteName} offers an informational founder personality self-assessment, type profiles, playbooks, optional
        email delivery, and export tools. Results are generated from your self-reported answers using our scoring model.
      </p>
      <p>
        <strong>Not professional advice.</strong> Content is for education and self-reflection only. It is not medical,
        psychological, legal, financial, or hiring advice. Do not use results as the sole basis for employment, investment,
        or clinical decisions.
      </p>
    </Section>

    <Section>
      <SectionTitle>Eligibility</SectionTitle>
      <p>
        You must be at least 16 years old (or the age of digital consent in your country) to use {LEGAL.siteName}. You
        represent that information you provide is accurate to the best of your knowledge.
      </p>
    </Section>

    <Section>
      <SectionTitle>Your account and device data</SectionTitle>
      <p>
        We do not require a traditional account. Quiz results are stored locally in your browser unless you email, export,
        or share them. You are responsible for safeguarding your device and any backups you create.
      </p>
    </Section>

    <Section>
      <SectionTitle>Acceptable use</SectionTitle>
      <p>You agree not to:</p>
      <ul>
        <li>Use the service for unlawful, harmful, or fraudulent purposes</li>
        <li>Attempt to reverse engineer, scrape, or overload the service</li>
        <li>Misrepresent assessment results as certified psychological evaluations</li>
        <li>Upload malicious files or interfere with other users&apos; access</li>
      </ul>
    </Section>

    <Section>
      <SectionTitle>Intellectual property</SectionTitle>
      <p>
        {LEGAL.siteName}, its branding, question sets, scoring model, copy, and design are owned by the operator or
        licensors. You may share your personal results and exported reports for personal or internal team use. You may not
        resell, republish, or commercially redistribute the assessment content or bulk type materials without permission.
      </p>
    </Section>

    <Section>
      <SectionTitle>Paid features</SectionTitle>
      <p>
        If payment features are enabled, prices and deliverables will be shown at checkout. Payments are processed by
        Stripe. Refund terms, if any, will be stated at purchase. When payments are disabled, the full report may be
        offered free of charge.
      </p>
    </Section>

    <Section>
      <SectionTitle>Disclaimers</SectionTitle>
      <p>
        THE SERVICE IS PROVIDED &quot;AS IS&quot; AND &quot;AS AVAILABLE&quot; WITHOUT WARRANTIES OF ANY KIND, EXPRESS OR
        IMPLIED, INCLUDING MERCHANTABILITY, FITNESS FOR A PARTICULAR PURPOSE, AND NON-INFRINGEMENT. WE DO NOT GUARANTEE
        THAT RESULTS WILL BE ACCURATE, COMPLETE, OR SUITABLE FOR YOUR SITUATION.
      </p>
    </Section>

    <Section>
      <SectionTitle>Limitation of liability</SectionTitle>
      <p>
        TO THE MAXIMUM EXTENT PERMITTED BY LAW, {LEGAL.siteName.toUpperCase()} AND ITS OPERATORS WILL NOT BE LIABLE FOR
        ANY INDIRECT, INCIDENTAL, SPECIAL, CONSEQUENTIAL, OR PUNITIVE DAMAGES, OR ANY LOSS OF PROFITS, DATA, OR GOODWILL,
        ARISING FROM YOUR USE OF THE SERVICE. OUR TOTAL LIABILITY FOR ANY CLAIM RELATING TO THE SERVICE IS LIMITED TO
        THE GREATER OF (A) AMOUNTS YOU PAID US IN THE 12 MONTHS BEFORE THE CLAIM OR (B) USD $50.
      </p>
    </Section>

    <Section>
      <SectionTitle>Termination</SectionTitle>
      <p>
        We may suspend or discontinue the service at any time. You may stop using the service at any time and delete local
        data from your browser or via in-app controls.
      </p>
    </Section>

    <Section>
      <SectionTitle>Governing law</SectionTitle>
      <p>
        These Terms are governed by applicable law where the operator of {LEGAL.siteName} is established, without regard
        to conflict of law rules, except where mandatory consumer protections in your country apply. Disputes should first
        be raised at <a href={`mailto:${LEGAL.contactEmail}`}>{LEGAL.contactEmail}</a>.
      </p>
    </Section>

    <Section>
      <SectionTitle>Contact</SectionTitle>
      <p>
        Questions about these Terms: <a href={`mailto:${LEGAL.contactEmail}`}>{LEGAL.contactEmail}</a>.
        See our <Link to={LEGAL_ROUTES.privacy}>Privacy Policy</Link> for data practices.
      </p>
    </Section>
  </LegalDocumentPage>
);

export default TermsOfService;
